<template>
  <HrPageLayout
    title="User Management"
    icon="👥"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'System Management' },
      { label: 'User Management' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click="loadAll" :disabled="loading">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
      <a href="#" class="hr-btn hr-btn-outline-primary" @click.prevent="$router.push('/category-role-management')">
        <i class="bx bx-shield-alt-2"></i> Access profiles & roles
      </a>
      <a href="#" class="hr-btn hr-btn-outline-primary" @click.prevent="$router.push('/employee-register')">
        <i class="bx bx-user-plus"></i> Employee Register
      </a>
    </template>

    <div class="user-management-content">
      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4" style="grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));">
        <div class="hr-metric-card gradient-1">
          <p class="hr-metric-label">Total Users</p>
          <h2 class="hr-metric-value">{{ stats.total || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-2">
          <p class="hr-metric-label">Active</p>
          <h2 class="hr-metric-value">{{ stats.active || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-3" style="cursor:pointer" @click="setPasswordFilter('expiring')" title="Filter expiring passwords">
          <p class="hr-metric-label">Password expiring (7d)</p>
          <h2 class="hr-metric-value">{{ stats.passwordExpiringSoon || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-4" style="cursor:pointer" @click="setPasswordFilter('expired')" title="Filter expired passwords">
          <p class="hr-metric-label">Password expired</p>
          <h2 class="hr-metric-value">{{ stats.passwordExpired || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-2" style="cursor:pointer" @click="setLoginFilter('never')" title="Never logged in">
          <p class="hr-metric-label">Never logged in</p>
          <h2 class="hr-metric-value">{{ stats.neverLoggedIn || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-3" style="cursor:pointer" @click="setStatusFilter('suspended')" title="Filter suspended accounts">
          <p class="hr-metric-label">Suspended</p>
          <h2 class="hr-metric-value">{{ stats.suspended || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-4" style="cursor:pointer" @click="setStatusFilter('blocked')" title="Filter blocked accounts">
          <p class="hr-metric-label">Blocked</p>
          <h2 class="hr-metric-value">{{ stats.blocked || 0 }}</h2>
        </div>
      </div>

      <!-- Assign access profiles -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-user-check"></i> Users</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading && !users.length" class="hr-loading-wrap hr-py-5">
            <div class="hr-spinner"></div>
            <p class="hr-text-muted hr-mt-2">Loading users and access profiles...</p>
          </div>

          <template v-else>
            <div v-if="loadError" class="hr-alert hr-alert-danger hr-mb-3">
              {{ loadError }}
            </div>
            <div class="hr-d-flex hr-gap-2 hr-mb-3 hr-flex-wrap">
              <button
                type="button"
                class="hr-btn hr-btn-sm"
                :class="!filterStatus && !passwordFilter && !loginFilter ? 'hr-btn-primary' : 'hr-btn-outline-secondary'"
                @click="setStatusFilter('')"
              >
                All users ({{ stats.total || 0 }})
              </button>
              <button
                type="button"
                class="hr-btn hr-btn-sm"
                :class="filterStatus === 'active' ? 'hr-btn-primary' : 'hr-btn-outline-secondary'"
                @click="setStatusFilter('active')"
              >
                Active users ({{ stats.active || 0 }})
              </button>
              <button
                type="button"
                class="hr-btn hr-btn-sm"
                :class="filterStatus === 'inactive' ? 'hr-btn-primary' : 'hr-btn-outline-secondary'"
                @click="setStatusFilter('inactive')"
              >
                Inactive users ({{ stats.inactive || 0 }})
              </button>
              <button
                type="button"
                class="hr-btn hr-btn-sm"
                :class="filterStatus === 'suspended' ? 'hr-btn-primary' : 'hr-btn-outline-secondary'"
                @click="setStatusFilter('suspended')"
              >
                Suspended ({{ stats.suspended || 0 }})
              </button>
              <button
                type="button"
                class="hr-btn hr-btn-sm"
                :class="filterStatus === 'blocked' ? 'hr-btn-primary' : 'hr-btn-outline-secondary'"
                @click="setStatusFilter('blocked')"
              >
                Blocked ({{ stats.blocked || 0 }})
              </button>
              <button
                type="button"
                class="hr-btn hr-btn-sm"
                :class="passwordFilter === 'expiring' ? 'hr-btn-primary' : 'hr-btn-outline-secondary'"
                @click="setPasswordFilter('expiring')"
              >
                Password expiring ({{ stats.passwordExpiringSoon || 0 }})
              </button>
              <button
                type="button"
                class="hr-btn hr-btn-sm"
                :class="passwordFilter === 'expired' ? 'hr-btn-primary' : 'hr-btn-outline-secondary'"
                @click="setPasswordFilter('expired')"
              >
                Password expired ({{ stats.passwordExpired || 0 }})
              </button>
              <button
                type="button"
                class="hr-btn hr-btn-sm"
                :class="loginFilter === 'never' ? 'hr-btn-primary' : 'hr-btn-outline-secondary'"
                @click="setLoginFilter('never')"
              >
                Never logged in ({{ stats.neverLoggedIn || 0 }})
              </button>
            </div>

            <!-- Filters -->
            <div class="hr-d-flex hr-flex-wrap hr-gap-2 hr-mb-4 hr-align-items-center">
              <input
                v-model="search"
                type="text"
                class="hr-form-control"
                placeholder="Search by name, username, email, ID, department..."
                style="max-width: 280px;"
                autocomplete="off"
                @input="debouncedLoadUsers"
              >
              <select v-model="filterCategory" class="hr-form-control" style="max-width: 200px;" @change="resetAndLoadUsers">
                <option value="">All profiles</option>
                <option v-for="c in activeCategories" :key="c.id" :value="c.categoryName">{{ c.categoryName }}</option>
              </select>
              <select v-model="filterStatus" class="hr-form-control" style="max-width: 140px;" @change="resetAndLoadUsers">
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
                <option value="inactive">Inactive</option>
                <option value="blocked">Blocked</option>
              </select>
              <select v-model="passwordFilter" class="hr-form-control" style="max-width: 180px;" @change="resetAndLoadUsers">
                <option value="">All passwords</option>
                <option value="expiring">Expiring within 7 days</option>
                <option value="expired">Expired</option>
              </select>
              <select v-model="loginFilter" class="hr-form-control" style="max-width: 160px;" @change="resetAndLoadUsers">
                <option value="">All logins</option>
                <option value="never">Never logged in</option>
                <option value="has_logged_in">Has logged in</option>
              </select>
              <span v-if="listRefreshing" class="hr-small hr-text-muted" aria-live="polite">Updating…</span>
            </div>

            <div
              ref="usersTableScroll"
              class="um-users-list"
              :class="{ 'is-refreshing': listRefreshing }"
            >
              <div v-if="users.length === 0" class="hr-text-center hr-py-5 hr-text-muted">
                No users found. Adjust filters or ensure users exist (create from Employee Register → Manage Account).
              </div>

              <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>User / Contact</th>
                    <th>Department</th>
                    <th>Access profile</th>
                    <th>Status</th>
                    <th>Password</th>
                    <th>Last login</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="u in users" :key="userRowKey(u)">
                    <td>
                      <div class="hr-fw-medium">{{ u.names || u.username || '—' }}</div>
                      <div class="hr-small hr-text-muted">{{ u.username }}</div>
                      <div class="hr-small hr-text-muted">{{ u.email || '—' }}</div>
                      <div v-if="u.idNo" class="hr-small hr-text-muted">ID: {{ u.idNo }}</div>
                    </td>
                    <td>{{ u.departmentName || '—' }}</td>
                    <td>
                      <template v-if="editingUserId === u.userID">
                        <select v-model="editCategoryId" class="hr-form-control hr-form-control-sm" style="min-width: 160px;">
                          <option value="">Select profile</option>
                          <option v-for="c in activeCategories" :key="c.id" :value="c.id">
                            {{ c.categoryName }}
                          </option>
                        </select>
                        <div v-if="editCategoryId && categoryRolesForEdit.length" class="hr-small hr-text-muted hr-mt-1">
                          Roles in this category: {{ categoryRolesForEdit.join(', ') }}
                        </div>
                      </template>
                      <template v-else>
                        <span class="hr-badge hr-badge-primary">{{ u.category || '—' }}</span>
                      </template>
                    </td>
                    <td>
                      <span :class="getStatusClass(u.status)">{{ u.status || '—' }}</span>
                    </td>
                    <td>
                      <div class="hr-small">
                        <span v-if="u.passwordExpired" class="hr-badge hr-badge-danger">Expired</span>
                        <span v-else-if="u.passwordExpiringSoon" class="hr-badge hr-badge-warning">
                          {{ u.passwordDaysRemaining === 0 ? 'Expires today' : (u.passwordDaysRemaining + ' day(s) left') }}
                        </span>
                        <span v-else class="hr-text-muted">{{ formatDate(u.passwordExpiry) }}</span>
                      </div>
                      <div class="hr-small hr-text-muted" v-if="u.passwordExpiry">{{ formatDate(u.passwordExpiry) }}</div>
                    </td>
                    <td>
                      <span v-if="u.neverLoggedIn" class="hr-badge hr-badge-secondary">Never</span>
                      <span v-else class="hr-small">{{ formatDateTime(u.lastLogin) }}</span>
                    </td>
                    <td>
                      <template v-if="editingUserId === u.userID">
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" :disabled="saving || !editCategoryId" @click="saveUserCategory(u)">
                          <span v-if="saving">Saving...</span>
                          <span v-else>Save</span>
                        </button>
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary" :disabled="saving" @click="cancelEdit">Cancel</button>
                      </template>
                      <template v-else>
                        <div class="hr-action-buttons">
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="startEdit(u)">
                            <i class="bx bx-edit"></i> Change profile
                          </button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary" @click="openManageAccount(u)" title="Manage account">
                            <i class="bx bx-user-circle"></i> Manage account
                          </button>
                          <button v-if="(u.status || '').toLowerCase() === 'suspended'" type="button" class="hr-btn hr-btn-sm hr-btn-success" :disabled="activatingUserId === u.userID" @click="activateUser(u)" title="Activate user">
                            <i class="bx bx-check-circle"></i> Activate
                          </button>
                          <button v-if="(u.status || '').toLowerCase() === 'blocked' || u.accountLockedUntil || u.failedLoginAttempts > 0" type="button" class="hr-btn hr-btn-sm hr-btn-warning" @click="unblockUser(u)" title="Unblock user">
                            <i class="bx bx-shield-quarter"></i> Unblock
                          </button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary" @click="openResetPassword(u)" title="Reset password">
                            <i class="bx bx-lock-open-alt"></i> Reset password
                          </button>
                          <button
                            v-if="u.passwordExpired"
                            type="button"
                            class="hr-btn hr-btn-sm hr-btn-danger"
                            :disabled="autoResettingId === u.userID"
                            @click="autoResetExpired(u)"
                            title="Auto-issue temporary password and notify user"
                          >
                            <i class="bx bx-reset"></i> Auto-reset expired
                          </button>
                        </div>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            </div>

            <div v-if="total > 0" class="hr-d-flex hr-flex-wrap hr-justify-content-between hr-align-items-center hr-gap-2 hr-mt-3">
              <span class="hr-text-muted hr-small">
                Showing {{ pageStart }}-{{ pageEnd }} of {{ total }} users (100 per page)
              </span>
              <div class="hr-d-flex hr-align-items-center hr-gap-2">
                <button
                  type="button"
                  class="hr-btn hr-btn-sm hr-btn-outline-secondary"
                  :disabled="currentPage <= 1 || listRefreshing"
                  @click="goToPage(currentPage - 1)"
                >
                  <i class="bx bx-chevron-left"></i> Previous
                </button>
                <span class="hr-small">Page {{ currentPage }} of {{ totalPages }}</span>
                <button
                  type="button"
                  class="hr-btn hr-btn-sm hr-btn-outline-secondary"
                  :disabled="currentPage >= totalPages || listRefreshing"
                  @click="goToPage(currentPage + 1)"
                >
                  Next <i class="bx bx-chevron-right"></i>
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Manage account modal - teleport to body so it appears above layout and isn't clipped -->
    <Teleport to="body">
      <UserManagementModal
        v-if="showUserModal && selectedEmployeeForModal"
        :employee="selectedEmployeeForModal"
        :existing-user="selectedUserForModal"
        :key="'um-' + (selectedUserForModal && selectedUserForModal.userID) + '-' + (selectedEmployeeForModal && selectedEmployeeForModal.id)"
        @close="showUserModal = false; selectedEmployeeForModal = null; selectedUserForModal = null"
        @user-updated="loadUsers({ soft: true, force: true }); loadStats()"
        @user-activated="loadUsers({ soft: true, force: true }); loadStats()"
        @user-suspended="loadUsers({ soft: true, force: true }); loadStats()"
        @user-created="loadUsers({ soft: true, force: true }); loadStats()"
        @user-deleted="loadUsers({ soft: true, force: true }); loadStats()"
      />
    </Teleport>

    <!-- Reset password modal -->
    <div v-if="showResetModal" class="hr-modal-overlay" @click.self="showResetModal = false">
      <div class="hr-modal-box hr-modal-sm">
        <div class="hr-modal-header">
          <h5 class="hr-modal-title"><i class="bx bx-lock-open-alt"></i> Reset password</h5>
          <button type="button" class="hr-modal-close" aria-label="Close" @click="showResetModal = false">&times;</button>
        </div>
        <div class="hr-modal-body">
          <p class="hr-text-muted hr-small" v-if="resetTargetUser">{{ resetTargetUser.names || resetTargetUser.username }} ({{ resetTargetUser.username }})</p>
          <div class="hr-mb-3">
            <label class="hr-form-label">New password</label>
            <input v-model="resetPasswordNew" :type="showResetPassword ? 'text' : 'password'" class="hr-form-control" placeholder="Enter new password">
          </div>
          <div class="hr-mb-3">
            <label class="hr-form-check">
              <input v-model="resetPasswordForceChange" type="checkbox">
              <span>Require change on next login</span>
            </label>
          </div>
        </div>
        <div class="hr-modal-footer">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showResetModal = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="resetPasswordSaving || !resetPasswordNew" @click="submitResetPassword">
            <span v-if="resetPasswordSaving">Resetting...</span>
            <span v-else>Reset password</span>
          </button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import UserManagementModal from "@/components/employeeRegister/modals/UserManagementModal.vue";

export default {
  name: "UserManagement",
  components: { HrPageLayout, UserManagementModal },
  data() {
    return {
      loading: false,
      listRefreshing: false,
      loadError: "",
      saving: false,
      stats: {
        total: 0,
        active: 0,
        inactive: 0,
        suspended: 0,
        pending: 0,
        passwordExpiringSoon: 0,
        passwordExpired: 0,
        neverLoggedIn: 0,
        byCategory: []
      },
      users: [],
      total: 0,
      currentPage: 1,
      pageSize: 100,
      categories: [],
      search: "",
      filterCategory: "",
      filterStatus: "",
      passwordFilter: "",
      loginFilter: "",
      editingUserId: null,
      editCategoryId: "",
      debounceTimer: null,
      usersLoadSeq: 0,
      lastUsersQueryKey: "",
      showUserModal: false,
      selectedEmployeeForModal: null,
      selectedUserForModal: null,
      showResetModal: false,
      resetTargetUser: null,
      resetPasswordNew: "",
      resetPasswordForceChange: true,
      resetPasswordSaving: false,
      showResetPassword: false,
      activatingUserId: null,
      autoResettingId: null
    };
  },
  computed: {
    activeCategories() {
      return (this.categories || []).filter(c => (c.status || "").toLowerCase() === "active");
    },
    categoryRolesForEdit() {
      if (!this.editCategoryId) return [];
      const c = this.categories.find(x => String(x.id) === String(this.editCategoryId));
      return (c && c.roles) ? c.roles : [];
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    pageStart() {
      return this.total === 0 ? 0 : ((this.currentPage - 1) * this.pageSize) + 1;
    },
    pageEnd() {
      return Math.min(this.currentPage * this.pageSize, this.total);
    }
  },
  mounted() {
    this.loadAll();
  },
  beforeUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  methods: {
    userRowKey(u) {
      const id = u?.userID != null && u.userID !== "" ? String(u.userID) : "";
      const un = u?.username != null ? String(u.username) : "";
      return id || un || `row-${u?.email || ""}-${u?.idNo || ""}`;
    },
    dedupeUsers(list) {
      if (!Array.isArray(list)) return [];
      const seen = new Set();
      const out = [];
      for (const row of list) {
        if (!row || typeof row !== "object") continue;
        const key = this.userRowKey(row);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(row);
      }
      return out;
    },
    usersQueryKey() {
      return [
        String(this.search || "").trim(),
        String(this.filterCategory || ""),
        String(this.filterStatus || ""),
        String(this.passwordFilter || ""),
        String(this.loginFilter || ""),
        String(this.currentPage)
      ].join("\0");
    },
    formatDate(d) {
      if (!d) return "—";
      return String(d).slice(0, 10);
    },
    formatDateTime(d) {
      if (!d) return "—";
      return String(d).replace("T", " ").slice(0, 19);
    },
    getStatusClass(status) {
      const s = (status || "").toLowerCase();
      if (s === "active") return "hr-badge hr-badge-success";
      if (s === "suspended") return "hr-badge hr-badge-warning";
      if (s === "pending") return "hr-badge hr-badge-info";
      return "hr-badge hr-badge-secondary";
    },
    async loadAll() {
      this.loading = true;
      this.loadError = "";
      try {
        await Promise.allSettled([
          this.loadStats(),
          this.loadCategories(),
          this.loadUsers({ soft: false, force: true })
        ]);
      } finally {
        this.loading = false;
      }
    },
    async loadStats() {
      try {
        const data = new FormData();
        data.append("function", "getAllUsersStats");
        const res = await execute(data, authUrl);
        
        if (typeof res?.data === 'string') {
          if (this.$toast) this.$toast.error("API returned a string instead of JSON for stats: " + res.data.substring(0, 50));
          console.error("API returned string:", res.data);
        }
        
        const d = res?.data?.data ?? res?.data ?? {};
        if (d && (d.total != null || d.active != null || Array.isArray(d.byCategory))) {
          this.stats = d;
        } else if (res?.data?.success && res?.data?.data) {
          this.stats = res.data.data;
        } else {
            if (this.$toast) this.$toast.error("Failed to parse stats. res.data = " + JSON.stringify(res?.data));
        }
      } catch (e) {
        console.error("getAllUsersStats failed", e);
        if (this.$toast) this.$toast.error("getAllUsersStats threw an error: " + e.message);
      }
    },
    async loadCategories() {
      try {
        const data = new FormData();
        data.append("function", "getCategories");
        const res = await execute(data, authUrl);
        const cats =
          res?.data?.data?.categories ??
          res?.data?.categories ??
          [];
        this.categories = Array.isArray(cats) ? cats : [];
      } catch (e) {
        console.error("getCategories failed", e);
        this.categories = [];
      }
    },
    debouncedLoadUsers() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      // Wait until typing pauses so intermediate ID searches don't thrash the table
      this.debounceTimer = setTimeout(() => this.resetAndLoadUsers(), 450);
    },
    resetAndLoadUsers() {
      this.currentPage = 1;
      this.loadUsers({ soft: true });
    },
    goToPage(page) {
      const nextPage = Math.min(Math.max(Number(page) || 1, 1), this.totalPages);
      if (nextPage === this.currentPage) return;
      this.currentPage = nextPage;
      this.loadUsers({ soft: true, force: true });
    },
    setStatusFilter(status) {
      this.filterStatus = status;
      this.passwordFilter = "";
      this.loginFilter = "";
      this.resetAndLoadUsers();
    },
    setPasswordFilter(f) {
      this.passwordFilter = this.passwordFilter === f ? "" : f;
      if (this.passwordFilter) {
        this.filterStatus = "";
        this.loginFilter = "";
      }
      this.resetAndLoadUsers();
    },
    setLoginFilter(f) {
      this.loginFilter = this.loginFilter === f ? "" : f;
      if (this.loginFilter) {
        this.filterStatus = "";
        this.passwordFilter = "";
      }
      this.resetAndLoadUsers();
    },
    async loadUsers(opts = {}) {
      const softOpt = opts.soft;
      const force = !!opts.force;
      const isSoft = softOpt !== undefined ? softOpt && this.users.length > 0 : this.users.length > 0;
      const queryKey = this.usersQueryKey();
      if (!force && queryKey === this.lastUsersQueryKey && this.users.length > 0 && !this.listRefreshing) {
        return;
      }

      const seq = ++this.usersLoadSeq;
      if (isSoft) this.listRefreshing = true;

      const scrollEl = this.$refs.usersTableScroll;
      const prevScroll = scrollEl ? scrollEl.scrollTop : 0;

      const data = new FormData();
      data.append("function", "getUsersList");
      data.append("limit", String(this.pageSize));
      data.append("offset", String((this.currentPage - 1) * this.pageSize));
      const q = String(this.search || "").trim();
      if (q) data.append("search", q);
      if (this.filterCategory) data.append("category", this.filterCategory);
      if (this.filterStatus) data.append("status", this.filterStatus);
      if (this.passwordFilter) data.append("passwordFilter", this.passwordFilter);
      if (this.loginFilter) data.append("loginFilter", this.loginFilter);
      try {
        const res = await execute(data, authUrl);
        // Drop stale responses from earlier searches/filter changes
        if (seq !== this.usersLoadSeq) return;

        const body = res?.data || {};
        const payload = body.data || body;
        let users =
          (Array.isArray(payload?.users) && payload.users) ||
          (Array.isArray(body?.users) && body.users) ||
          (Array.isArray(payload) && payload) ||
          [];
        users = this.dedupeUsers(users);

        if (body.success === false) {
          // Keep previous rows on soft refresh so the page does not blink empty
          if (!isSoft) {
            this.users = [];
            this.total = 0;
          }
          this.loadError = body.message || "Failed to load users (getUsersList).";
          return;
        }

        this.users = users;
        this.total = Number(payload?.total ?? body?.total ?? users.length) || users.length;
        this.lastUsersQueryKey = queryKey;
        this.loadError = "";
        if (users.length === 0 && body.success !== true && body.success !== false) {
          this.loadError = "Unexpected response from getUsersList. Check API path.";
        }

        this.$nextTick(() => {
          const el = this.$refs.usersTableScroll;
          if (el && isSoft) el.scrollTop = prevScroll;
        });
      } catch (e) {
        if (seq !== this.usersLoadSeq) return;
        console.error("getUsersList failed", e);
        if (!isSoft) {
          this.users = [];
          this.total = 0;
        }
        this.loadError =
          e?.response?.data?.message ||
          e?.message ||
          "Network error loading users.";
      } finally {
        if (seq === this.usersLoadSeq) {
          this.listRefreshing = false;
        }
      }
    },
    async autoResetExpired(u) {
      if (!u?.userID) return;
      if (!window.confirm(`Auto-reset expired password for ${u.names || u.username}? A temporary password will be emailed/SMS'd to the user.`)) {
        return;
      }
      this.autoResettingId = u.userID;
      try {
        const data = new FormData();
        data.append("function", "autoResetExpiredUserPassword");
        data.append("userId", String(u.userID));
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          if (this.$toast) this.$toast.success(res.data.message || "Temporary password sent.");
          else alert(res.data.message || "Temporary password sent to user.");
          await this.loadUsers({ soft: true, force: true });
          await this.loadStats();
        } else {
          if (this.$toast) this.$toast.error(res?.data?.message || "Auto-reset failed");
          else alert(res?.data?.message || "Auto-reset failed");
        }
      } catch (e) {
        alert(e?.message || "Auto-reset failed");
      } finally {
        this.autoResettingId = null;
      }
    },
    startEdit(u) {
      this.editingUserId = u.userID;
      this.editCategoryId = u.categoryId != null ? String(u.categoryId) : "";
    },
    cancelEdit() {
      this.editingUserId = null;
      this.editCategoryId = "";
    },
    async saveUserCategory(u) {
      const cat = this.activeCategories.find(x => String(x.id) === String(this.editCategoryId)) || this.categories.find(x => String(x.id) === String(this.editCategoryId));
      if (!cat) return;
      this.saving = true;
      const data = new FormData();
      data.append("function", "updateUserAccount");
      data.append("userId", u.userID);
      data.append("status", u.status || "active");
      data.append("category", cat.categoryName);
      data.append("categoryId", cat.id);
      data.append("requireOTP", u.requireOTP || "false");
      try {
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.cancelEdit();
          await this.loadUsers({ soft: true, force: true });
          await this.loadStats();
          if (this.$toast) this.$toast.success(res.data.message || "Access profile updated.");
          else alert("Access profile updated. User will have the roles assigned to " + cat.categoryName + ".");
        } else {
          if (this.$toast) this.$toast.error(res.data.message || "Update failed.");
          else alert(res?.data?.message || "Update failed.");
        }
      } catch (e) {
        if (this.$toast) this.$toast.error("Update failed: " + (e.message || "Unknown error"));
        else alert("Update failed: " + (e.message || "Unknown error"));
      } finally {
        this.saving = false;
      }
    },
    openManageAccount(u) {
      const userId = u && (u.userID ?? u.userId);
      if (!u || userId == null || String(userId) === "" || String(userId) === "0") {
        if (this.$toast) this.$toast.warning("Invalid user row.");
        else alert("Invalid user row.");
        return;
      }
      // Prefer linked employee id; keep a stable stub for the modal header if missing.
      // Never depend on employeeID alone — many users are not linked to employees.
      const empId = u.employeeID ?? u.employeeId ?? u.employee_id ?? null;
      const parts = (u.names || u.username || "").trim().split(/\s+/);
      const row = { ...u, userID: userId };
      this.selectedUserForModal = row;
      this.selectedEmployeeForModal = {
        id: empId != null && String(empId).trim() !== "" && String(empId) !== "0" ? (Number(empId) || empId) : null,
        userID: userId,
        firstName: parts[0] || u.username || "",
        lastName: parts.slice(1).join(" ") || "",
        empNo: u.username || u.idNo || "",
        nationalId: u.idNo || "",
        email: u.email || "",
        departmentName: u.departmentName || "",
        positionTitle: ""
      };
      this.showUserModal = true;
    },
    openResetPassword(u) {
      this.resetTargetUser = u;
      this.resetPasswordNew = "";
      this.resetPasswordForceChange = true;
      this.showResetModal = true;
    },
    async submitResetPassword() {
      if (!this.resetTargetUser || !this.resetPasswordNew) return;
      this.resetPasswordSaving = true;
      const data = new FormData();
      data.append("function", "resetUserPassword");
      data.append("userId", this.resetTargetUser.userID);
      data.append("newPassword", this.resetPasswordNew);
      data.append("forceChange", this.resetPasswordForceChange ? "true" : "false");
      try {
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          if (this.$toast) this.$toast.success(res.data.message || "Password reset.");
          else alert("Password reset.");
          this.showResetModal = false;
          this.resetTargetUser = null;
          this.resetPasswordNew = "";
        } else {
          if (this.$toast) this.$toast.error(res.data.message || "Reset failed.");
          else alert(res?.data?.message || "Reset failed.");
        }
      } catch (e) {
        if (this.$toast) this.$toast.error("Reset failed: " + (e.message || "Unknown error"));
        else alert("Reset failed: " + (e.message || "Unknown error"));
      } finally {
        this.resetPasswordSaving = false;
      }
    },
    async activateUser(u) {
      if (!confirm("Activate this user? They will be able to log in again.")) return;
      this.activatingUserId = u.userID;
      const data = new FormData();
      data.append("function", "activateUserAccount");
      data.append("userId", u.userID);
      try {
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          if (this.$toast) this.$toast.success(res.data.message || "User activated.");
          else alert("User activated.");
          await this.loadUsers({ soft: true, force: true });
          await this.loadStats();
        } else {
          if (this.$toast) this.$toast.error(res.data.message || "Activate failed.");
          else alert(res?.data?.message || "Activate failed.");
        }
      } catch (e) {
        if (this.$toast) this.$toast.error("Activate failed: " + (e.message || "Unknown error"));
        else alert("Activate failed: " + (e.message || "Unknown error"));
      } finally {
        this.activatingUserId = null;
      }
    },
    async unblockUser(u) {
      if (!confirm("Unblock this user? Their failed login attempts will be reset and they will be able to log in with their current password.")) return;
      const data = new FormData();
      data.append("function", "unblockUserAccount");
      data.append("userId", u.userID);
      try {
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          if (this.$toast) this.$toast.success(res.data.message || "User unblocked.");
          else alert("User unblocked.");
          await this.loadUsers({ soft: true, force: true });
          await this.loadStats();
        } else {
          if (this.$toast) this.$toast.error(res.data.message || "Unblock failed.");
          else alert(res?.data?.message || "Unblock failed.");
        }
      } catch (e) {
        if (this.$toast) this.$toast.error("Unblock failed: " + (e.message || "Unknown error"));
        else alert("Unblock failed: " + (e.message || "Unknown error"));
      }
    }
  }
};
</script>

<style scoped>
/* ─── Enhanced User Management Styling ──────────────────── */
.user-management-content {
  padding: 0;
  animation: fadeIn 0.35s ease-out;
}
.um-users-list {
  min-height: 120px;
  transition: opacity 0.15s ease;
}
.um-users-list.is-refreshing {
  opacity: 0.72;
  pointer-events: none;
}
.hr-list-refresh-bar {
  font-size: 12px;
  font-weight: 600;
  color: #047857;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  padding: 6px 10px;
  margin-bottom: 10px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hr-mr-1 {
  margin-right: 0.25rem;
}

/* ─── Enhanced Table Wrapper ──────────────────────────────── */
.hr-table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

/* ─── Enhanced Table Styling ──────────────────────────────── */
.hr-table {
  animation: slideUp 0.4s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hr-table thead {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.hr-table thead th {
  padding: 16px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0;
}

.hr-table tbody tr {
  transition: all 0.2s ease;
  border-bottom: 1px solid #f1f5f9;
}

.hr-table tbody tr:hover {
  background: linear-gradient(90deg, #f0f9ff 0%, #f8fafc 100%);
  transform: scale(1.001);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.08);
}

.hr-table tbody td {
  padding: 16px;
  vertical-align: middle;
}

/* ─── Enhanced User Info Cell ──────────────────────────────── */
.hr-table tbody td .hr-fw-medium {
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
  margin-bottom: 4px;
}

.hr-table tbody td .hr-small {
  font-size: 12px;
  line-height: 1.6;
}

/* ─── Enhanced Status Badges ──────────────────────────────── */
.hr-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
  animation: fadeIn 0.3s ease-out;
}

.hr-badge-primary {
  background: linear-gradient(135deg, #dbeafe 0%, #93c5fd 100%);
  color: #1e40af;
  box-shadow: 0 2px 6px rgba(59, 130, 246, 0.2);
}

.hr-badge-success {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #047857;
  box-shadow: 0 2px 6px rgba(5, 150, 105, 0.2);
}

.hr-badge-warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde047 100%);
  color: #92400e;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.2);
}

.hr-badge-danger {
  background: linear-gradient(135deg, #fee2e2 0%, #fca5a5 100%);
  color: #991b1b;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.2);
}

.hr-badge-secondary {
  background: linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%);
  color: #475569;
  box-shadow: 0 2px 6px rgba(100, 116, 139, 0.2);
}

/* ─── Enhanced Action Buttons ──────────────────────────────── */
.hr-action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.hr-action-buttons .hr-btn {
  transition: all 0.2s ease;
}

.hr-action-buttons .hr-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.hr-action-buttons .hr-btn-outline-primary {
  border-color: #3b82f6;
  color: #3b82f6;
}

.hr-action-buttons .hr-btn-outline-primary:hover {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
  border-color: #3b82f6;
}

.hr-action-buttons .hr-btn-outline-secondary {
  border-color: #cbd5e1;
  color: #64748b;
}

.hr-action-buttons .hr-btn-outline-secondary:hover {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
  color: #fff;
  border-color: #64748b;
}

.hr-action-buttons .hr-btn-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  border: none;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.hr-action-buttons .hr-btn-success:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

/* ─── Enhanced Filter Buttons ──────────────────────────────── */
.hr-d-flex .hr-btn-sm {
  transition: all 0.2s ease;
  border-radius: 20px;
  padding: 8px 16px;
  font-weight: 600;
}

.hr-d-flex .hr-btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
  border: none;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.hr-d-flex .hr-btn-primary:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.hr-d-flex .hr-btn-outline-secondary {
  background: #fff;
  border: 1.5px solid #cbd5e1;
  color: #64748b;
}

.hr-d-flex .hr-btn-outline-secondary:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #475569;
}

/* ─── Enhanced Form Controls ──────────────────────────────── */
.hr-form-control {
  padding: 10px 14px;
  border: 1.5px solid #cbd5e1;
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.hr-form-control:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  outline: none;
}

.hr-form-control-sm {
  padding: 8px 12px;
  font-size: 13px;
  border-radius: 8px;
}

/* ─── Enhanced Empty State ──────────────────────────────────── */
.hr-text-center.hr-py-5 {
  padding: 80px 20px;
  animation: fadeIn 0.5s ease-out;
}

.hr-text-center.hr-py-5::before {
  content: '📭';
  display: block;
  font-size: 64px;
  margin-bottom: 16px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
}

/* ─── Enhanced Loading State ──────────────────────────────── */
.hr-loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.hr-spinner {
  width: 32px;
  height: 32px;
  border: 4px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ─── Enhanced Modal Styling ──────────────────────────────── */
.hr-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  animation: fadeIn 0.3s ease-out;
  backdrop-filter: blur(2px);
}

.hr-modal-box {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
}

.hr-modal-sm {
  width: 460px;
}

.hr-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 2px solid #e2e8f0;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
  border-radius: 16px 16px 0 0;
}

.hr-modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 10px;
}

.hr-modal-title i {
  font-size: 24px;
  color: #3b82f6;
}

.hr-modal-close {
  background: #f1f5f9;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.hr-modal-close:hover {
  background: #e2e8f0;
  color: #1e293b;
  transform: rotate(90deg);
}

.hr-modal-body {
  padding: 24px;
  overflow: auto;
}

.hr-modal-footer {
  padding: 16px 24px;
  border-top: 2px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: linear-gradient(180deg, #fff 0%, #f8fafc 100%);
  border-radius: 0 0 16px 16px;
}

/* ─── Enhanced Form Elements in Modal ──────────────────────── */
.hr-modal-body .hr-form-label {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 6px;
  display: block;
}

.hr-modal-body .hr-form-control {
  width: 100%;
}

.hr-modal-body .hr-form-check {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  transition: background 0.2s ease;
}

.hr-modal-body .hr-form-check:hover {
  background: #f8fafc;
}

.hr-modal-body .hr-form-check input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* ─── Enhanced Buttons in Modal ──────────────────────────── */
.hr-modal-footer .hr-btn {
  padding: 10px 20px;
  font-weight: 600;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.hr-modal-footer .hr-btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
  border: none;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.hr-modal-footer .hr-btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.hr-modal-footer .hr-btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.hr-modal-footer .hr-btn-secondary {
  background: #fff;
  color: #64748b;
  border: 1.5px solid #cbd5e1;
}

.hr-modal-footer .hr-btn-secondary:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #475569;
}

/* ─── Responsive Design ──────────────────────────────────── */
@media (max-width: 768px) {
  .hr-action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .hr-action-buttons .hr-btn {
    width: 100%;
    justify-content: center;
  }

  .hr-modal-sm {
    width: 95vw;
  }

  .hr-d-flex.hr-flex-wrap {
    flex-direction: column;
  }

  .hr-form-control {
    max-width: 100% !important;
  }
}
</style>
